import { Alert, Card, Empty, Flex, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { AssetCustomFieldModal } from '~/app/components/asset-custom-field/AssetCustomFieldModal';
import { AssetCustomFieldTableCard } from '~/app/components/asset-custom-field/AssetCustomFieldTableCard';
import type { AssetCustomFieldsPageViewProps } from './AssetCustomFieldsPage.types';

const { Paragraph, Text, Title } = Typography;

export function AssetCustomFieldsPageView({
  assetTypeOptions,
  assetTypesErrorMessage,
  closeFieldModal,
  confirmDeleteField,
  editingField,
  fieldForm,
  fieldModalOpen,
  fieldTypeOptions,
  fields,
  fieldsError,
  isLoadingAssetTypes,
  isLoadingFields,
  isSubmitting,
  onAssetTypeChange,
  onCreateField,
  onEditField,
  selectedAssetTypeId,
  selectedAssetTypeLabel,
  submitFieldForm,
  unitOptions,
}: AssetCustomFieldsPageViewProps) {
  const { t } = useTranslation(['assetCustomFields', 'assets']);

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('assets:customFields.title', 'Asset Custom Fields')}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t(
            'assets:customFields.description',
            'Select an asset type and manage the custom metadata fields exposed in the platform.',
          )}
        </Paragraph>
      </div>

      <Card style={{ borderRadius: 16 }}>
        <Flex vertical gap={8}>
          <Text strong>
            {t('assets:customFields.assetTypeLabel', 'Asset type')}
          </Text>
          <Select
            showSearch
            optionFilterProp="label"
            value={selectedAssetTypeId || undefined}
            options={assetTypeOptions}
            loading={isLoadingAssetTypes}
            onChange={onAssetTypeChange}
          />
          <Text type="secondary">
            {selectedAssetTypeLabel
              ? t(
                  'assets:customFields.assetTypeHelpSelected',
                  'Managing custom fields for {{assetType}}.',
                  { assetType: selectedAssetTypeLabel },
                )
              : t(
                  'assets:customFields.assetTypeHelp',
                  'Custom fields are scoped to the selected asset type.',
                )}
          </Text>
        </Flex>
      </Card>

      {assetTypesErrorMessage ? (
        <Card style={{ borderRadius: 16 }}>
          <Alert
            type="error"
            showIcon
            message={t(
              'assets:customFields.assetTypesLoadFailed',
              'Failed to load asset types.',
            )}
            description={assetTypesErrorMessage}
          />
        </Card>
      ) : assetTypeOptions.length === 0 && !isLoadingAssetTypes ? (
        <Card style={{ borderRadius: 16 }}>
          <Empty
            description={t(
              'assets:customFields.noAssetTypes',
              'No asset types are available yet.',
            )}
          />
        </Card>
      ) : (
        <AssetCustomFieldTableCard
          fields={fields}
          isLoading={isLoadingFields}
          error={fieldsError}
          onCreate={onCreateField}
          onEdit={onEditField}
          onDelete={confirmDeleteField}
        />
      )}

      <AssetCustomFieldModal
        open={fieldModalOpen}
        editingField={editingField}
        fieldForm={fieldForm}
        fieldTypeOptions={fieldTypeOptions}
        unitOptions={unitOptions}
        isSubmitting={isSubmitting}
        onSubmit={submitFieldForm}
        onCancel={closeFieldModal}
      />
    </Space>
  );
}
